import { useCallback } from 'react';
import { BlockBlobClient } from '@azure/storage-blob';
import {
  BulkGenerateUploadUrlDto,
  DocumentUploadStage,
  extractErrorMessage,
  sleep,
  useGetDocumentUploadStatus,
  useCreateDocumentUploadUrls,
} from '@sib/api';
import { useUserSetupStore } from '@sib/auth';
import { useTranslation } from '@sib/i18n';
import { useNotification } from '@sib/notifications';
import { FileItem } from '@sib/types';
import { v4 as uuidv4 } from 'uuid';
import { FileUploadWithCallbacksProps } from './types';
import {
  CHUNK_SIZE,
  STATUS_CHECK_INTERVAL,
  STATUS_RETRY_INTERVAL,
  MAX_RETRIES,
  isAbortError,
  computeFileMd5,
} from './utils';

interface UploadToBlobStorageProps extends FileUploadWithCallbacksProps {}

type PostprocessFileOptions = Pick<
  BulkGenerateUploadUrlDto,
  'batchId' | 'totalFiles'
> & {
  abortSignal?: AbortSignal;
};

export const useUploadToBlobStorage = ({
  onProgress,
  onError,
}: UploadToBlobStorageProps) => {
  const { t } = useTranslation();
  const {
    organization: { id: organizationId },
  } = useUserSetupStore();
  const { mutateAsync: createDocumentUploadUrls } = useCreateDocumentUploadUrls(
    {
      organizationId,
    }
  );
  const generateFilesWithUrls = useCallback(
    async (fileItems: FileItem[], options: PostprocessFileOptions) => {
      const { batchId, totalFiles } = options;
      try {
        fileItems.forEach(fileItem => {
          onProgress?.(fileItem, 5);
        });

        const { items, failed } = await createDocumentUploadUrls({
          files: fileItems.map(fileItem => ({
            filename: fileItem.file.name,
            filesize: fileItem.file.size,
            mimetype: fileItem.file.type,
            sensitive: fileItem.isSensitive ?? false,
          })),
          batchId,
          totalFiles,
        });

        const failedFilenames = new Set(failed.map(f => f.filename));

        failed.forEach(failedItem => {
          const fileItem = fileItems.find(
            fileItem => fileItem.file.name === failedItem.filename
          );
          if (fileItem) onError?.(fileItem, failedItem.error);
        });

        return fileItems
          .filter(fileItem => !failedFilenames.has(fileItem.file.name))
          .map(fileItem => ({
            ...fileItem,
            uploadUrl:
              items.find(item => item.filename === fileItem.file.name)
                ?.uploadUrl || '',
          }));
      } catch (error) {
        const message = extractErrorMessage(
          error,
          t('fileManager.uploadFailedMessage'),
          { defaultMessageStatusCodes: [500, 404] }
        );

        fileItems.forEach(fileItem => onError?.(fileItem, message, error));

        return [];
      }
    },
    [onError, onProgress, t, createDocumentUploadUrls]
  );

  const uploadFileToBlobStorage = useCallback(
    async (fileItem: FileItem, options: PostprocessFileOptions) => {
      const { file, uploadUrl = '' } = fileItem;

      const { abortSignal } = options;

      onProgress?.(fileItem, 10);

      try {
        const md5Hash = await computeFileMd5(file);
        const blobClient = new BlockBlobClient(uploadUrl);

        await blobClient.uploadData(file, {
          blockSize: CHUNK_SIZE,
          concurrency: 4,
          abortSignal,
          blobHTTPHeaders: {
            blobContentMD5: md5Hash,
          },
          onProgress: ({ loadedBytes }) => {
            const uploadProgress = Math.round((loadedBytes / file.size) * 100);
            const totalProgress = 10 + Math.round((uploadProgress * 80) / 100);
            onProgress?.(fileItem, totalProgress);
          },
        });

        onProgress?.(fileItem, 95);
      } catch (error) {
        if (isAbortError(error)) return;

        onError?.(
          fileItem,
          extractErrorMessage(error, t('fileManager.uploadFailedMessage')),
          error
        );
      }
    },
    [onError, onProgress, t]
  );

  return {
    generateFilesWithUrls,
    uploadFileToBlobStorage,
  };
};

interface UseBatchUploadProps extends FileUploadWithCallbacksProps {
  getFiles: () => FileItem[];
}

export const useBatchUpload = ({
  getFiles,
  onSuccess,
  onError,
}: UseBatchUploadProps) => {
  const {
    organization: { id: organizationId },
  } = useUserSetupStore();
  const { open } = useNotification();
  const { mutateAsync: getDocumentUploadStatus } = useGetDocumentUploadStatus();
  const { t } = useTranslation();

  const generateBatchId = () => {
    const batchUploadId = uuidv4();

    return batchUploadId;
  };

  const checkForUploadStatus = useCallback(
    async (batchId: string, interval: number = STATUS_CHECK_INTERVAL) => {
      const pollStatus = async (
        currentBatchId: string,
        currentInterval: number,
        attempt: number = 0
      ) => {
        try {
          const { items } = await getDocumentUploadStatus({
            organizationId: organizationId || '',
            batchId: currentBatchId,
          });
          const files = getFiles();

          files.forEach((fileItem: FileItem) => {
            const found = items.find(item => item.name === fileItem.file.name);
            if (!found || found.status === DocumentUploadStage.Pending) return;

            const isUploaded = found.status === DocumentUploadStage.Uploaded;

            if (isUploaded) return onSuccess?.({ ...fileItem, progress: 100 });

            onError?.(
              { ...fileItem, progress: 100 },
              t('fileManager.uploadFailedMessage')
            );
          });

          const areAllFilesProcessed = items.every(item =>
            [DocumentUploadStage.Failed, DocumentUploadStage.Uploaded].includes(
              item.status
            )
          );

          if (!areAllFilesProcessed) {
            await sleep(currentInterval);

            return pollStatus(currentBatchId, currentInterval, 0);
          }
        } catch (error) {
          if (attempt < MAX_RETRIES) {
            return pollStatus(
              currentBatchId,
              STATUS_RETRY_INTERVAL,
              attempt + 1
            );
          }
          open?.({
            type: 'error',
            message: t('fileManager.actions.checkUploadStatus.error.message'),
            description: t(
              'fileManager.actions.checkUploadStatus.error.description'
            ),
          });

          return error;
        }
      };

      return pollStatus(batchId, interval);
    },
    [
      getDocumentUploadStatus,
      getFiles,
      onError,
      onSuccess,
      t,
      organizationId,
      open,
    ]
  );

  return {
    generateBatchId,
    checkForUploadStatus,
  };
};
